import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { TrustSignals } from '../components/TrustSignals';
import { DegreeCategoryGrid } from '../components/DegreeCategoryGrid';
import { SEOContentSection } from '../components/SEOContentSection';
import { FAQSection } from '../components/FAQSection';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { slowScrollToElement } from '../utils/smoothScroll';
export function HomePage() {
  usePageMeta('FindOnlineColleges.org | Compare Accredited Online Degree Programs 2026', 'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget. No cost to you.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  const navigate = useNavigate();
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Track selected values for the content section
  const [selectedDegree, setSelectedDegree] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  // On a phone the matches sit a full screen below the picker, so nothing
  // appears to happen when a program is chosen. Only a deliberate selection
  // arms the scroll — arriving on a /degree/... link should not move the page.
  const resultsRef = useRef<HTMLDivElement>(null);
  const scrollWhenReadyRef = useRef(false);
  const fetchResults = useCallback(async (degreeInterest: string, areaStudyStr: string, mProgram: string) => {
    // Only search if mProgram (specialization) is selected
    if (!mProgram) {
      setHasSearched(false);
      return;
    }
    setSelectedDegree(degreeInterest);
    setSelectedArea(areaStudyStr);
    setSelectedProgram(mProgram);
    // Update URL if not already matching
    if (areaStudyStr && mProgram && (areaStudy !== areaStudyStr || program !== mProgram)) {
      navigate(`/degree/${areaStudyStr}/${mProgram}`, {
        replace: true
      });
    }
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudyStr || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [navigate, areaStudy, program]);
  const handleSelectionChange = useCallback((degreeInterest: string, areaStudyStr: string, mProgram: string, source?: string) => {
    // Only the program choice (or submitting the form) walks the visitor down.
    // Changing degree level or area of study is still mid-decision.
    if (mProgram && (source === 'specialization' || source === 'submit')) {
      scrollWhenReadyRef.current = true;
    }
    fetchResults(degreeInterest, areaStudyStr, mProgram);
  }, [fetchResults]);
  // On mount, if params exist, search
  useEffect(() => {
    if (areaStudy && program) {
      fetchResults('', areaStudy, program);
    }
  }, [areaStudy, program, fetchResults]);
  // Once the first real match is on screen, walk the visitor down to it.
  useEffect(() => {
    if (!scrollWhenReadyRef.current) return;
    if (isLoading || apiError || matchedSchools.length === 0) return;
    scrollWhenReadyRef.current = false;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile || !resultsRef.current) return;
    slowScrollToElement(resultsRef.current, {
      offset: 96,
      duration: 1100
    });
  }, [isLoading, apiError, matchedSchools.length]);
  return <main className="flex-grow bg-light" data-id="element-2146" data-mp="3t5faabxvw8u9">
      <HeroSection onSelectionChange={handleSelectionChange} data-id="element-2147" data-mp="3o7dqfosmw5hq" />
      <TrustSignals data-id="element-2148" data-mp="3k6k3axn0mupw" />

      {hasSearched ? <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-2149" data-mp="3k7bqbk2jrk4k">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-2150" data-mp="2csqgh7fyi31a">
            {/* Left Column: Results */}
            <div className="lg:w-2/3" data-id="element-2151" data-mp="28rmyaeafruww">
              {/* Loading State */}
              {isLoading && <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-2152" data-mp="o9ck703ssv22">
                  <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-2153" data-mp="sa66xq5i83td" />
                  <p className="text-gray-600 font-medium" data-id="element-2154" data-mp="xaamndyl8uij">
                    Finding your best school matches...
                  </p>
                </div>}

              {/* Error State */}
              {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-2155" data-mp="170n42xb3w7kg">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2156" data-mp="1bxvpr7vgqtqg" />
                  <div data-id="element-2157" data-mp="1g0a7tprb5pgo">
                    <p className="text-red-800 font-medium" data-id="element-2158" data-mp="1g1tmjbxbfsgc">{apiError}</p>
                    <p className="text-red-600 text-sm mt-1" data-id="element-2159" data-mp="1kzv9w91rydxm">
                      Try adjusting your search criteria or check back later.
                    </p>
                  </div>
                </div>}

              {/* Results */}
              {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" ref={resultsRef} data-id="element-2160" data-mp="tvpysjdu5duo">
                  {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-2161" data-mp="oy6lpmsvj74b" data-mpi={idx}>
                      <MatchedSchoolCard school={school} index={idx} data-id="element-2162" data-mp="12xg2ankzs38t" />
                      {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-2163" data-mp="xx1pks8g454g" />}
                    </Fragment>)}
                </div>}

              {/* Empty State */}
              {!isLoading && !apiError && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-2164" data-mp="17x2dkrh9f9ez">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-2165" data-mp="1lnxffjtccq92">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-2166" data-mp="1hn58ix7xyxfy" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-2167" data-mp="1vmwov4lxkpx4">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-2168" data-mp="1qolfeuzjshff">
                    Try adjusting your degree level or program selection to find
                    more results.
                  </p>
                </div>}
            </div>

            {/* Right Column: Inline Lead Capture */}
            <div className="lg:w-1/3" data-id="element-2169" data-mp="209uw7bf3h06l">
              <HomeInlineLeadCapture data-id="element-2170" data-mp="2597ediv0tj2u" />
            </div>
          </div>

          {/* Dynamic Program Content Section */}
          {!isLoading && !apiError && selectedProgram && <ProgramContentSection degreeInterest={selectedDegree} areaOfStudy={selectedArea} specialization={selectedProgram} data-id="element-2171" data-mp="1rfzmdq312lkm" />}
        </div> : <>
          <DegreeCategoryGrid data-id="element-2172" data-mp="1hkeoa312y5g5" />
          <SEOContentSection data-id="element-2173" data-mp="1hlg22f9hd61k" />
          <FAQSection data-id="element-2174" data-mp="1hi20vacjz2vp" />
        </>}
    </main>;
}